// src/components/header/article/HeaderContent.tsx
'use client';

import { ArticleHeaderBackBtn, ArticleHeaderFuncBtn } from './BtnClient/Btn';
import Avatar from '@/components/ui/avatar/Avatar';
import Link from 'next/link';
import Image from 'next/image';
import type { Article } from '@/model/article';

import { IconArFill } from '@/components/icons';

// 工具
import { formatMessageTimeLabel } from '@/libs/utils/time';

type ShowMode = 'always' | 'desktop-only' | 'mobile-only' | 'never';

export type HeaderContentProps = {
  /** 是否顯示返回按鈕（預設 true） */
  showBackBtn?: boolean;
  /** 控制 BackBtn 在哪些裝置顯示（預設與 showBackBtn 對齊） */
  backBtnShow?: ShowMode;
  /** BackBtn 外層 wrapper 的額外 className */
  backBtnClassName?: string;
  /** 父層直接傳入文章（新版 API 一定要傳） */
  article?: Article;
  /** slug：例如 collection_682（僅作識別 / 除錯用） */
  articleId?: string;
  /** 心情/狀態前綴文字（預設「覺得」） */
  moodText?: string;
  /** for debug：從哪個呼叫點來（方便 console 區分） */
  sourceLabel?: string;
  onClose?: () => void;
};

function visibilityClass(mode: ShowMode) {
  switch (mode) {
    case 'desktop-only':
      return 'd-none d-md-inline';
    case 'mobile-only':
      return 'd-inline d-md-none';
    case 'never':
      return 'd-none';
    case 'always':
    default:
      return 'd-inline';
  }
}

export default function HeaderContent({
  showBackBtn = true,
  backBtnShow = showBackBtn ? 'always' : 'never',
  backBtnClassName,
  article,
  // articleId,
  moodText = '覺得',
  // sourceLabel,
  onClose,
}: HeaderContentProps) {
  const backWrapperClass = ['btn-back-arrow', visibilityClass(backBtnShow), backBtnClassName]
    .filter(Boolean)
    .join(' ');

  // 允許 article 暫時為 undefined，全部用安全取值
  const a = article;

  // -------------------------------------------------------------------
  // 判斷是不是店家文章（多一點保險條件）
  // -------------------------------------------------------------------
  const isStoreArticle =
    a?.bizType === 'store_post' ||
    a?.table === 'store_article' ||
    !!(a?.storeOpenId && a.storeName);

  // 店家 slug（店家頁用）
  const storeSlug = (a?.storeOpenId ?? '').toString().trim();

  // userSlug：優先用 womoId，其次用 author.id
  const userSlug = (a?.womoId || a?.author?.id || '').trim();

  const createdTime = a?.createdAt || '';

  // Avatar src：店家文章優先用 storePhoto，其次 author.avatar
  const rawStorePhoto = (a?.storePhoto || '').trim();
  const rawAuthorAvatar = (a?.author?.avatar || '').trim();
  const hasAvatarImage = !!(rawStorePhoto || rawAuthorAvatar);
  const avatarSrc = hasAvatarImage ? rawAuthorAvatar || rawStorePhoto : undefined;

  // -------------------------------------------------------------------
  // 顯示名稱：
  //  - 店家文章：storeName -> author.name -> womoId -> '店家'
  //  - 一般文章：author.name -> womoId -> '匿名'
  // -------------------------------------------------------------------
  const storeDisplayName = (a?.storeName || a?.author?.name || a?.womoId || '').trim();
  const userDisplayName = ((a?.author?.name || a?.womoId || '').trim() || '匿名') as string;
  const displayName = isStoreArticle ? storeDisplayName || '店家' : userDisplayName;

  // emoji：只要 text 或 pic 有一個存在就顯示
  const emojiTextRaw = (a?.emoji?.text || '').trim();
  const emojiPic = (a?.emoji?.pic || '').trim();
  const hasEmoji = !!(emojiTextRaw || emojiPic);
  const emojiText = emojiTextRaw || '';

  // Debug：檢查來源與顯示資料
  // if (typeof window !== 'undefined') {
  //   console.log('[HeaderContent] source =', sourceLabel ?? 'unknown', 'article =', article);
  //   console.log('[HeaderContent][debug]', {
  //     source: sourceLabel,
  //     articleId,
  //     isStoreArticle,
  //     table: a?.table,
  //     bizType: a?.bizType,
  //     storeName: a?.storeName,
  //     storeOpenId: a?.storeOpenId,
  //     womoId: a?.womoId,
  //     authorName: a?.author?.name,
  //     displayName,
  //   });
  // }

  return (
    <div className="d-flex justify-content-between align-items-center py-3">
      <div className="d-flex align-items-center flex-shrink-1 flex-grow-1 min-w-0">
        {/* Back 按鈕（以 wrapper 的顯示工具類控制） */}
        {showBackBtn && (
          <div className={`d-md-none ms-2 ${backWrapperClass}`}>
            <ArticleHeaderBackBtn onClose={onClose} />
          </div>
        )}

        {/* 使用者 / 店家資訊區塊 */}
        <div className="d-flex align-items-center flex-shrink-1 flex-grow-1 min-w-0 ms-2 ms-md-3">
          {/* 頭像：店家文章連到 store，其它文章連到 user(womoId 優先)；沒圖就畫灰底圓形 */}
          {isStoreArticle && storeSlug ? (
            <Link href={`/store/${storeSlug}`}>
              {hasAvatarImage ? (
                <Avatar src={avatarSrc!} size={42} />
              ) : (
                <div
                  className="d-inline-block rounded-circle bg-grey"
                  style={{ width: 42, height: 42 }}
                />
              )}
            </Link>
          ) : (
            <Link href={userSlug ? `/user/${userSlug}` : '#'}>
              {hasAvatarImage ? (
                <Avatar src={avatarSrc!} size={42} />
              ) : (
                <div
                  className="d-inline-block rounded-circle bg-grey"
                  style={{ width: 42, height: 42 }}
                />
              )}
            </Link>
          )}

          <div className="text-start ms-2 flex-shrink-1 flex-grow-1 min-w-0">
            <h1 className="d-flex align-items-center fs-sm-1">
              {/* 顯示名稱：店家文章顯示店名，其他顯示作者名稱，皆可點連結 */}
              <span className="fw-bold flex-shrink-1 text-truncate-1 min-w-0">
                {isStoreArticle && storeSlug ? (
                  <Link href={`/store/${storeSlug}`}>{displayName}</Link>
                ) : (
                  <Link href={userSlug ? `/user/${userSlug}` : '#'}>{displayName}</Link>
                )}
              </span>

              {/* 心情 emoji 區塊 */}
              {hasEmoji && (
                <>
                  <span className="d-flex align-items-center flex-shrink-0 ms-1 fs-sm-1 text-muted">
                    {' — '}
                    {moodText} {emojiText && <span>{emojiText} </span>}
                    {emojiPic && (
                      <Image
                        src={emojiPic}
                        alt={emojiText || 'emoji'}
                        width={20}
                        height={20}
                        className="ms-1 align-text-bottom"
                      />
                    )}
                  </span>
                </>
              )}
            </h1>

            {/* 時間 + AR 打卡標示 */}
            <div className="d-flex align-items-center text-green fs-sm-2">
              <span className="text-grey-700 flex-shrink-0">
                {formatMessageTimeLabel(createdTime)}
                {a?.ar?.isAR && '．'}
              </span>
              {a?.ar?.isAR && (
                <span className="d-inline-flex align-items-center flex-grow-1 flex-shrink-1 min-w-0">
                  <Link
                    href={`/ar-space/${a.ar.arSpaceId}/?type=${a.ar.arType}`}
                    className="d-flex align-items-center w-100"
                  >
                    <span className="flex-shrink-0 me-1">
                      <IconArFill width={22} />
                    </span>
                    <span className="flex-shrink-1 text-truncate-1 flex-grow-1 min-w-0">
                      {a.ar.arTitle}空間打卡
                    </span>
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* 右側功能選單：內部自己處理 ShareModal */}
      <div className="flex-shrink-0 flex-grow-0">
        <ArticleHeaderFuncBtn article={a} />
      </div>
    </div>
  );
}
